import React, { ChangeEvent, useEffect, useState } from 'react';
import { collection, doc, DocumentData, getDoc, getDocs, getFirestore, setDoc } from 'firebase/firestore';
import { getStorage, ref, UploadResult, uploadString } from 'firebase/storage';
import { Alert, Avatar, Button, Card, CardActionArea, CardActions, CardContent, CardMedia, Chip, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControl, InputLabel, List, ListItemButton, ListItemText, MenuItem, Modal, Paper, Select, SelectChangeEvent, Stack, styled, Tab, Table, TableBody, TableCell, TableContainer, TableRow, Tabs, TextField, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from '../../store';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/system';
import { AttachFileRounded, BadgeRounded, EditRounded, Upload } from '@mui/icons-material';
import { TabPanel } from '../TabPanel';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { queryEmployeeDocuments } from '../../utils/queryEmployeeDocuments';
import { queryEmployeeFromStateOrDb } from '../../utils/queryEmployeeFromStateOrDb';
import { Employee } from '../../types/Employee';
import { SecureComponent } from '../../security/SecureComponent';
import { EmployeeForm } from './EmployeeForm';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const tabValues = [
  'info',
  'documents',
  'requests',
  'salary-deduction'
];

export interface EmployeePageContext {
  employee: Employee;
}

export const EmployeePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useLocation();
  const { idNumber } = useParams();
  const [employee, setEmployee] = useState<Employee | undefined>();
  const [openEditForm, setOpenEditForm] = useState(false);

  const paths = location.pathname.split('/');
  let tab = 0;
  if (paths.length >= 5) {
    tab = tabValues.indexOf(paths[4]);
    tab = tab !== -1 ? tab : 0;
  }

  const handleSelectedTabOnChange = (event: React.SyntheticEvent, tabIndex: number) => {
    navigate(
      `/home/employees/${idNumber}/${tabValues[tabIndex]}`, 
      { 
        replace: true,
        state: {
          employee
        },
      }
    )
  };

  const handleUpdateInformationOnClick = () => setOpenEditForm(true);

  useEffect(() => {
    queryEmployeeFromStateOrDb(state, idNumber).then((e) => {
      setEmployee(e);
    });
  }, [idNumber]);

  if (!employee) {
    return (
      <Box>
        <Alert severity='info'>
          Getting employee information...
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Container disableGutters>
        <Paper 
          sx={{ 
            mt: 2, 
            mb: 4, 
            p: 3,
            position: 'relative',
            overflow: 'hidden',
          }}
          elevation={4}
        >
          <div style={{
            backgroundImage: `url(${employee.picture})`, 
            backgroundSize: 'cover',
            backgroundPosition: 'center center', 
            filter: 'blur(5px)',
            position: 'absolute',
            left: 0,
            bottom: 0,
            right: 0,
            top: 0,
            zIndex: 0,
          }} />
          <div style={{ display: 'flex', flexDirection: 'row', position: 'relative', zIndex: 100 }}>
            <Avatar alt={employee.name} src={employee.picture} sx={{ width: 56, height: 56 }}/>
            <Box ml={4} flexGrow={1}>
              <Typography variant="h4">{employee.name}</Typography>
              <Box display="flex" flexDirection="row">
                <BadgeRounded color="info" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} color="#808080">{employee.idNumber}</Typography>
              </Box>
            </Box>
          </div>
        </Paper>
        <Box>
          <Stack direction='row' spacing={1}>
            <Tabs value={tab} onChange={handleSelectedTabOnChange} aria-label="basic tabs example">
              <Tab label="Info" {...a11yProps(0)} />
              <Tab label="Documents" {...a11yProps(1)} />
              <Tab label="Requests" {...a11yProps(2)} />
              <Tab label="Salary Deduction" {...a11yProps(3)} />
            </Tabs>
            <Box flexGrow={1} />
            <SecureComponent permissions={['update.employees']}>
              <Button startIcon={<EditRounded />} color='warning' onClick={handleUpdateInformationOnClick}>
                Update Information
              </Button>
            </SecureComponent>
          </Stack>
          <Box sx={{ py: 2 }}>
            <Outlet context={{employee}} />
          </Box>
        </Box>
      </Container>
      <SecureComponent permissions={['create.employees', 'update.employees']}>
        <EmployeeForm 
          open={openEditForm} 
          employee={employee}
          onClose={() => setOpenEditForm(false)}
          onSave={(e) => {
            setEmployee(e);
            setOpenEditForm(false);
          }} />
      </SecureComponent>
    </>
  );
};
